import { TAKE_COPY } from "../lib/captures/take-copy";
import { type Project, projectDisplayName } from "../lib/projects/schema";

interface TakeProjectPickerProps {
  projects: Project[];
  /** `useCurrentProject().loading` : chargement en cours, rien à montrer tant
   *  qu'on ne sait pas si la liste est vraiment vide. */
  loading: boolean;
  /** `useCurrentProject().erreur` : dernier chargement en échec, ou null. */
  erreur: string | null;
  value: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
}

/**
 * Choix du projet d'un enregistrement conservé sans projet connu (C13, DS
 * §6.11 : l'action qui lève l'obstacle, dans la carte qui conserve). Patron
 * de l'ancienne `OrphanRecordingCard` (label + `<select>` natif, qui ouvre le picker iOS),
 * tutoyé et lu dans `TAKE_COPY`. Une liste vide n'est vraie que si le
 * chargement des projets a fini SANS échec (17 août : un « 200 [] » pendant le
 * boot a été lu comme « aucun projet » — `project-context.tsx`) : pendant le
 * chargement, rien ; en cas d'échec, notre phrase d'échec générique. Aucun
 * projet accessible une fois chargé : on le dit, sans rien offrir — l'audio
 * reste sur le téléphone.
 */
export function TakeProjectPicker({
  projects,
  loading,
  erreur,
  value,
  onChange,
  disabled,
}: TakeProjectPickerProps) {
  if (loading) return null;
  if (erreur !== null) {
    return (
      <p role="alert" className="m-0 text-red-deep">
        {TAKE_COPY.actionFailed}
      </p>
    );
  }
  if (projects.length === 0) {
    return <p className="m-0 text-muted">{TAKE_COPY.noProjectAvailable}</p>;
  }
  return (
    <>
      <p className="m-0 text-muted">{TAKE_COPY.chooseProject}</p>
      <label className="flex flex-col gap-1">
        <span className="text-small font-medium text-flambeau-900">{TAKE_COPY.projectLabel}</span>
        <select
          aria-label={TAKE_COPY.projectSelectLabel}
          className="rounded-lg border border-line bg-surface px-3 py-2 text-small"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{TAKE_COPY.projectPlaceholder}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {projectDisplayName(p)}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}
